import { HttpHeaders, HttpParams, HttpContext, HttpClient } from '@angular/common/http';
import { inject, DestroyRef, Injector, InjectionToken, signal, assertInInjectionContext } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { selectObjectProps, replaceTags, isNullish, isObject, assign, isJSType, wrapToArray, NOOP } from '@noda-lib/utils/handlers';
import { Subject, takeUntil, finalize, filter, firstValueFrom, take } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
function getFromRoute(keys, type, activatedRoute) {
  const paramsType = type === 'query' ? 'queryParams' : 'params';
  return selectObjectProps(activatedRoute.snapshot[paramsType], keys);
}
function routeParams(...keys) {
  const activatedRoute = inject(ActivatedRoute);
  return {
    params: getFromRoute(keys, 'params', activatedRoute)
  };
}
function routeQuery(...keys) {
  const activatedRoute = inject(ActivatedRoute);
  return {
    query: getFromRoute(keys, 'query', activatedRoute)
  };
}
function routeQueryParams(paramsKeys, queryKeys) {
  const activatedRoute = inject(ActivatedRoute);
  return {
    params: getFromRoute(paramsKeys, 'params', activatedRoute),
    query: getFromRoute(queryKeys, 'query', activatedRoute)
  };
}
function toHttpHeaders(object, headers = new HttpHeaders()) {
  const filledHeaders = Object.entries(object).reduce((accum, [key, value]) => {
    accum = accum.append(key, String(value));
    return accum;
  }, headers);
  return filledHeaders;
}
function toHttpParams(query) {
  const params = new HttpParams({
    fromObject: query
  });
  return params;
}
function toHttpContext(token, ctx) {
  return new HttpContext().set(token, ctx);
}
function getQueryParam(paramKey, path = window.location.href) {
  const url = new URL(path);
  return url.searchParams.get(paramKey);
}
function isExistQueryParams(params, path = window.location.href) {
  const url = new URL(path);
  const isValidUrl = params.every(param => url.searchParams.has(param));
  return isValidUrl;
}
function makeURL(startUrl, query, params) {
  if (params) {
    startUrl = replaceTags(startUrl, params);
  }
  const url = new URL(startUrl);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (!isNullish(value)) {
        url.searchParams.append(key, String(value));
      }
    });
  }
  return url.toString();
}
function getHostTag(tagUrl) {
  const [tag] = tagUrl.split('/');
  return tag;
}
function makeTagsURL(url, query, params) {
  if (params) {
    url = replaceTags(url, params);
  }
  if (query) {
    const queryParams = new URLSearchParams(query);
    url = `${url}?${queryParams.toString()}`;
  }
  return url;
}
const REQUESTS_INJECTION_FIELDS = ['connect', 'onDestroy'];

/* eslint-disable @typescript-eslint/dot-notation */
class ApiHubManager {
  constructor(hub, injector) {
    this.hub = hub;
    this.injector = injector;
    this.initContext();
  }
  initContext() {
    Object.keys(this.hub).forEach(key => {
      this.hub[key]['initContext'](this.injector);
    });
  }
  initConfigInUseContext(cfg) {
    if (cfg.onDestroy) this.initOnDestroy(cfg.onDestroy);
    if (cfg.cache) this.initCacheFields(cfg.cache);
    if (cfg.force) this.initForceFields(cfg.force);
  }
  initCacheFields(fields) {
    fields.forEach(field => this.hub[field]['sendRequestIfCan']());
  }
  initForceFields(fields) {
    fields.forEach(field => this.hub[field]['sendRequestIfCan']());
  }
  initOnDestroy(onDestroy) {
    const destroyRef = inject(DestroyRef);
    destroyRef.onDestroy(() => {
      if (onDestroy.abort) this.executeOperation(parseExecutionFields(onDestroy.abort), 'abort');
      if (onDestroy.reset) this.executeOperation(parseExecutionFields(onDestroy.reset), 'reset');
    });
  }
  executeOperation(keys, operation) {
    keys = keys.length ? keys : Object.keys(this.hub);
    keys.forEach(key => this.hub[key][operation]());
  }
  abort(...keys) {
    this.executeOperation(keys, 'abort');
  }
  clear(...keys) {
    this.executeOperation(keys, 'clear');
  }
  reset(...keys) {
    this.executeOperation(keys, 'reset');
  }
  destroy(...keys) {
    this.executeOperation(keys, 'destroy');
  }
}

/* eslint-disable @typescript-eslint/naming-convention */
function checkAndThrowConnectConfigError(meta, fieldsList = REQUESTS_INJECTION_FIELDS) {
  if (isObject(meta)) {
    const injectionField = fieldsList.find(field => field in meta);
    if (injectionField) {
      throw new Error(`"${injectionField}" should be used only in the injection context`);
    }
  }
}
function mergeInjectionFields(additionalFields) {
  return REQUESTS_INJECTION_FIELDS.concat(additionalFields);
}
function checkForReservedHubKeys(hub) {
  const isExistReservedKeys = new Set(Object.keys(hub)).has('hub');
  if (isExistReservedKeys) throw new Error('ApiHub cannot contain the following reserved keys for requests: "hub"');
}
function createApiHubFactory(Hub) {
  return () => {
    const instance = new Hub();
    checkForReservedHubKeys(instance);
    const injector = inject(Injector);
    const manager = new ApiHubManager(instance, injector);
    return assign(instance, {
      hub: manager
    });
  };
}
function parseExecutionFields(fields) {
  return isJSType(fields, 'boolean') ? [] : fields;
}
const ROOT_API_HUB = new InjectionToken('ROOT_API_HUB');

/* eslint-disable @typescript-eslint/dot-notation */
/* eslint-disable @typescript-eslint/naming-convention */
function provideRootApiHub(Hub) {
  return {
    provide: ROOT_API_HUB,
    useFactory: createApiHubFactory(Hub)
  };
}
function provideApiHub(Hub) {
  return {
    provide: Hub,
    useFactory: createApiHubFactory(Hub)
  };
}
function useRootApiHub(config) {
  const hub = inject(ROOT_API_HUB);
  if (config) hub.hub['initConfigInUseContext'](config);
  return hub;
}
function useApiHub(Hub, config) {
  const hub = inject(Hub);
  if (config) hub.hub['initConfigInUseContext'](config);
  return hub;
}
class ApiRequest {
  constructor(urlOrMeta, context = null) {
    this.context = context;
    this.meta = null;
    this.http = null;
    this.lastRequest$ = null;
    this.connections = new Set();
    this.abort$ = new Subject();
    this.loading$ = signal(false);
    this.error$ = signal(null);
    this.loading = this.loading$.asReadonly();
    this.error = this.error$.asReadonly();
    this.initMeta(urlOrMeta);
    if (context) this.init(context);
  }
  initMeta(urlOrMeta) {
    this.meta = isJSType(urlOrMeta, 'string') ? {
      url: urlOrMeta
    } : assign(urlOrMeta);
  }
  init(context) {
    this.initContext(context);
    if (this.meta.onDestroy) this.initDestroy(context, this.meta.onDestroy);
    if (this.meta.connect) this.connect(this.meta.connect);
  }
  initContext(context) {
    this.http = context.get(HttpClient);
  }
  initDestroy(context, config) {
    const {
      abort,
      reset
    } = config;
    context.get(DestroyRef).onDestroy(() => {
      if (abort) this.abort();
      if (reset) this.reset();
    });
  }
  makeUrl(opts) {
    const url = makeTagsURL(this.meta.url, opts?.query, opts?.params);
    return url;
  }
  executeWith(response, connectWith) {
    connectWith = wrapToArray(connectWith);
    connectWith.forEach(item => item.set(response));
  }
  executeReload(reload) {
    reload = wrapToArray(reload);
    reload.forEach(request => request.reload());
  }
  handleConnection(response, connect) {
    if (connect?.with) this.executeWith(response, connect?.with);
    if (connect?.reload) this.executeReload(connect.reload);
    connect?.next?.(response);
  }
  reload() {
    const request$ = this.lastRequest$ || this.request();
    this.sendRequest(request$);
  }
  sendRequest(request$, opts) {
    const {
      stream = NOOP
    } = assign(this.meta.sendOptions, opts?.sendOptions);
    this.loading$.set(true);
    this.lastRequest$ = request$;
    return request$.pipe(stream, takeUntil(this.abort$), finalize(() => this.loading$.set(false))).subscribe({
      next: response => this.handleNext(response, opts?.connect),
      error: error => this.handleError(error, opts?.connect),
      complete: () => this.handleComplete(opts?.connect)
    });
  }
  handleNext(response, connect) {
    this.error$.set(null);
    if (this.meta.transform) response = this.meta.transform(response);
    this.handleConnection(response, connect);
    this.connections.forEach(connection => this.handleConnection(response, connection));
  }
  handleReload(reloadRequests) {
    reloadRequests = wrapToArray(reloadRequests);
    reloadRequests.forEach(req => req.reload());
  }
  handleError(error, observer) {
    this.error$.set(error);
    observer?.error?.(error);
    this.connections.forEach(connection => {
      connection.error?.(error);
    });
  }
  handleComplete(observer) {
    observer?.complete?.();
    this.connections.forEach(connection => {
      connection.complete?.();
    });
  }
  connect(connection) {
    const destroyRef = inject(DestroyRef);
    const disconnect = () => this.disconnectObserver(connection);
    this.connections.add(connection);
    destroyRef.onDestroy(disconnect);
    return {
      disconnect
    };
  }
  disconnect() {
    if (this.connections.has(this.meta?.connect)) this.disconnectObserver(this.meta?.connect);
  }
  disconnectObserver(observer) {
    this.connections.delete(observer);
  }
  abort() {
    this.abort$.next();
  }
  clear() {
    this.error$.set(null);
  }
  reset() {
    this.lastRequest$ = null;
    this.abort();
    this.clear();
  }
  destroy() {
    this.reset();
    this.connections.clear();
  }
}
class FetchRequest extends ApiRequest {
  constructor(urlOrMeta, context = null) {
    super(urlOrMeta, context);
    this.loaded = signal(false);
    this.value = signal(this.initialValue);
  }
  get initialValue() {
    return this.meta.initialValue ?? null;
  }
  get isInitialized() {
    return this.loading() || this.loaded();
  }
  request(opts) {
    const url = this.makeUrl(opts?.urlOptions);
    return this.http.get(url, opts?.requestOptions);
  }
  send(opts) {
    return this.sendRequest(this.request(opts), opts);
  }
  handleNext(response, observer) {
    this.loaded.set(true);
    this.value.set(response);
    super.handleNext(response, observer);
  }
  clear() {
    super.clear();
    this.value.set(this.initialValue);
  }
  reset() {
    super.reset();
    this.loaded.set(false);
  }
}
class CacheRequest extends FetchRequest {
  constructor(urlOrMeta, context = null) {
    super(urlOrMeta, context);
    this.extractCacheMetaOptions(this.meta);
  }
  extractCacheMetaOptions(meta) {
    if (!isJSType(meta, 'string') && meta.cache) {
      this.cacheOptions = meta.cache;
    }
  }
  sendRequestIfCan() {
    if (!this.isInitialized) {
      const options = assign(this.cacheOptions);
      this.send(options);
    }
  }
  setUrlOptions(params) {
    this.cacheOptions = {
      urlOptions: params.urlOptions
    };
    if (params?.withRequest) {
      const options = assign(this.cacheOptions, params.withRequest);
      this.send(options);
    }
  }
  cache() {
    this.sendRequestIfCan();
    return this;
  }
  reset() {
    super.reset();
    this.cacheOptions = null;
  }
}
function useCache(meta) {
  try {
    assertInInjectionContext(useCache);
    const context = inject(Injector);
    return new CacheRequest(meta, context);
  } catch (e) {
    checkAndThrowConnectConfigError(meta);
    return new CacheRequest(meta);
  }
}
class DeleteRequest extends ApiRequest {
  request(opts) {
    const url = this.makeUrl(opts?.urlOptions);
    return this.http.delete(url, opts?.requestOptions);
  }
  send(opts) {
    return this.sendRequest(this.request(opts), opts);
  }
}
function useDelete(meta) {
  try {
    assertInInjectionContext(useDelete);
    const context = inject(Injector);
    return new DeleteRequest(meta, context);
  } catch (e) {
    checkAndThrowConnectConfigError(meta);
    return new DeleteRequest(meta);
  }
}
const GET_REQUESTS_INJECTION_FIELDS = ['force'];
class GetRequest extends FetchRequest {
  constructor(urlOrMeta, context = null) {
    super(urlOrMeta, context);
    this.sendRequestIfForce();
  }
  sendRequestIfForce() {
    const {
      force
    } = this.meta;
    if (force) {
      const options = isJSType(force, 'boolean') ? null : force;
      this.send(options);
    }
  }
  sendRequestIfCan(opts) {
    if (!this.isInitialized) {
      this.send(opts);
    }
  }
  load(opts) {
    this.sendRequestIfCan(opts);
    const request$ = toObservable(this.value, {
      injector: this.context
    }).pipe(filter(_ => this.loaded()));
    const value$ = opts?.valueLike === 'promise' ? firstValueFrom(request$) : request$.pipe(take(1));
    return value$;
  }
}
function useGet(meta) {
  try {
    assertInInjectionContext(useGet);
    const context = inject(Injector);
    return new GetRequest(meta, context);
  } catch (e) {
    checkAndThrowConnectConfigError(meta, mergeInjectionFields(GET_REQUESTS_INJECTION_FIELDS));
    return new GetRequest(meta);
  }
}
class PatchRequest extends ApiRequest {
  request(body, opts) {
    const url = this.makeUrl(opts?.urlOptions);
    return this.http.patch(url, body, opts?.requestOptions);
  }
  send(body, opts) {
    return this.sendRequest(this.request(body, opts), opts);
  }
}
function usePatch(meta) {
  try {
    assertInInjectionContext(usePatch);
    const context = inject(Injector);
    return new PatchRequest(meta, context);
  } catch (e) {
    checkAndThrowConnectConfigError(meta);
    return new PatchRequest(meta);
  }
}
class PostRequest extends ApiRequest {
  request(body, opts) {
    const url = this.makeUrl(opts?.urlOptions);
    return this.http.post(url, body, opts?.requestOptions);
  }
  send(body, opts) {
    return this.sendRequest(this.request(body, opts), opts);
  }
}
function usePost(meta) {
  try {
    assertInInjectionContext(usePost);
    const context = inject(Injector);
    return new PostRequest(meta, context);
  } catch (e) {
    checkAndThrowConnectConfigError(meta);
    return new PostRequest(meta);
  }
}
class PutRequest extends ApiRequest {
  request(body, opts) {
    const url = this.makeUrl(opts?.urlOptions);
    return this.http.put(url, body, opts?.requestOptions);
  }
  send(body, opts) {
    return this.sendRequest(this.request(body, opts), opts);
  }
}
function usePut(meta) {
  try {
    assertInInjectionContext(usePut);
    const context = inject(Injector);
    return new PutRequest(meta, context);
  } catch (e) {
    checkAndThrowConnectConfigError(meta);
    return new PutRequest(meta);
  }
}

// export * from './features';

/**
 * Generated bundle index. Do not edit.
 */

export { ApiHubManager, CacheRequest, DeleteRequest, GetRequest, PatchRequest, PostRequest, PutRequest, getFromRoute, getHostTag, getQueryParam, isExistQueryParams, makeTagsURL, makeURL, provideApiHub, provideRootApiHub, routeParams, routeQuery, routeQueryParams, toHttpContext, toHttpHeaders, toHttpParams, useApiHub, useCache, useDelete, useGet, usePatch, usePost, usePut, useRootApiHub };
